import React, { useState, useRef, useEffect } from "react";
import { Search, ChevronDown, ChevronUp, Check, Link2, ArrowRight } from "lucide-react";
import { Spinner, buildUniversalExtractUrl, copyTextToClipboard } from "../constants";
import QuickStart from "./QuickStart";
import ApiIndicator from "./ApiIndicator";
import { docTypeStyle } from "./studioConst";
import { SX, FRAME, TX } from "./studioTokens";

const CATEGORIES = [
  { id: "all", label: "All Templates" },
  { id: "invoice", label: "Invoice" },
  { id: "contract", label: "Contracts" },
  { id: "receipt", label: "Receipts" },
  { id: "purchase_order", label: "Purchase Orders" },
];

const SORT_OPTIONS = [
  { id: "recent", label: "Recently Updated" },
  { id: "oldest", label: "Oldest Updated" },
  { id: "name", label: "Name (A–Z)" },
];

const Row = ({ tpl, onOpen }) => {
  const s = docTypeStyle(tpl.document_type);
  const [copied, setCopied] = useState(false);
  const copyEndpoint = (e) => {
    e.stopPropagation();
    if (!tpl.slug) return;
    copyTextToClipboard(buildUniversalExtractUrl(tpl.slug)).catch(() => {});
    setCopied(true); setTimeout(() => setCopied(false), 1600);
  };
  return (
    <div className={`group flex flex-col gap-3 px-4 py-[13px] cursor-pointer sm:flex-row sm:items-center sm:gap-4 ${SX.cardSm} ${SX.cardHover}`} onClick={() => onOpen(tpl)}>
      <div className="min-w-0 sm:min-w-[200px]">
        <div className={`${TX.rowTitle} group-hover:text-[#893F82] transition-colors`}>{tpl.name}</div>
        {tpl.slug && <div className="text-[11.5px] text-[#52525B] mt-0.5 font-mono truncate">{tpl.slug}</div>}
      </div>
      <div className="flex flex-wrap items-center gap-2 sm:contents">
        <span className={`${SX.badge} ${s.badge}`}>{s.label}</span>
        <span className="text-[12.5px] text-[#52525B] sm:w-16">{tpl.fields?.length || 0} Fields</span>
        <span className="hidden sm:block flex-1" />
        <span className="text-[12.5px] text-[#52525B] whitespace-nowrap">Updated {tpl.updated_at ? new Date(tpl.updated_at).toLocaleDateString() : "—"}</span>
        {tpl.slug && (
          <button onClick={copyEndpoint} title="Copy the extraction endpoint for this template"
            className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] font-semibold flex-shrink-0 transition-colors ${copied ? "border-[#E9D2E6] bg-[#FBF5FA] text-[#893F82]" : "border-[#ECE9EE] bg-white text-[#52525B] hover:bg-[#F5F5F5]"}`}>
            {copied ? <><Check size={11} /> Copied</> : <><Link2 size={11} /> Copy Endpoint</>}
          </button>
        )}
        <ArrowRight size={15} className="text-[#A04A97] flex-shrink-0 ml-auto" />
      </div>
    </div>
  );
};

/* 03 · Library — category rail + template list, the widest content frame in the
   app (1340px) on the same 24px canvas inset as every other screen. */
const StudioLibrary = ({ templates, loading, billingBalance, loadingBalance, onOpenTemplate, onManageUsage }) => {
  const [cat, setCat] = useState("all");
  const [query, setQuery] = useState("");
  const [sortBy, setSortBy] = useState("recent");
  const [sortOpen, setSortOpen] = useState(false);
  const sortRef = useRef(null);

  useEffect(() => {
    if (!sortOpen) return;
    const h = (e) => { if (sortRef.current && !sortRef.current.contains(e.target)) setSortOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [sortOpen]);

  const counts = {};
  CATEGORIES.forEach((c) => { counts[c.id] = c.id === "all" ? templates.length : templates.filter((t) => t.document_type === c.id).length; });

  const filtered = templates.filter((t) => {
    if (cat !== "all" && t.document_type !== cat) return false;
    if (query.trim()) {
      const q = query.toLowerCase();
      return (t.name || "").toLowerCase().includes(q) || (t.slug || "").toLowerCase().includes(q);
    }
    return true;
  });
  const at = (t) => new Date(t.updated_at || 0).getTime();
  const sorted = [...filtered].sort((a, b) => {
    if (sortBy === "name") return (a.name || "").localeCompare(b.name || "");
    if (sortBy === "oldest") return at(a) - at(b);
    return at(b) - at(a);
  });

  return (
    <div className={`flex-1 overflow-auto ${FRAME.canvas} p-4 sm:p-6`}>
      <div className={`${FRAME.library} grid grid-cols-1 gap-5 xl:grid-cols-[250px_minmax(0,1fr)] xl:items-start`}>
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-1">
          <div className={`w-full py-[18px] px-3.5 ${SX.card}`}>
            <div className={`${TX.overline} px-2 pb-2.5`}>Categories</div>
            {CATEGORIES.map((c) => (
              <div key={c.id} onClick={() => setCat(c.id)}
                className={`flex items-center justify-between gap-2 px-2.5 py-2 rounded-lg cursor-pointer mb-0.5 text-[13.5px] transition-colors ${
                  cat === c.id ? "bg-[#FBF5FA] text-[#893F82] font-semibold" : "text-[#1A1A1A] font-medium hover:bg-[#F5F5F5]"
                }`}>
                <span>{c.label}</span>
                <span className={`text-[11.5px] font-semibold ${cat === c.id ? "text-[#893F82]" : "text-[#8A8A90]"}`}>{counts[c.id]}</span>
              </div>
            ))}
          </div>

          <ApiIndicator balance={billingBalance} loading={loadingBalance} onManage={onManageUsage} />
        </div>

        <div className="flex-1 min-w-0 flex flex-col gap-6">
          <QuickStart onSelectTemplate={onOpenTemplate} />

          <div>
            <div className="flex flex-col gap-3 mb-4 lg:flex-row lg:items-center lg:justify-between">
              <div className={`${TX.sectionHeading} flex-shrink-0`}>Your Templates ({sorted.length})</div>
              <div className="flex flex-col gap-2.5 sm:flex-row sm:items-center">
                <div className="flex items-center gap-2 px-3 py-2 border border-[#ECE9EE] rounded-[9px] bg-white w-full sm:w-[230px] transition-colors focus-within:border-[#A04A97] focus-within:shadow-[0_0_0_3px_rgba(160,74,151,0.14)]">
                  <Search size={14} className="text-[#8A8A90] flex-shrink-0" />
                  <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search templates…"
                    className="border-none outline-none text-[12.5px] text-[#1A1A1A] placeholder:text-[#8A8A90] w-full bg-transparent" />
                </div>
                <div ref={sortRef} className="relative">
                  <button onClick={() => setSortOpen((o) => !o)}
                    className={`flex w-full items-center justify-between gap-2 px-3 py-2 border border-[#ECE9EE] rounded-[9px] text-[#1A1A1A] text-[12.5px] font-medium whitespace-nowrap transition-colors sm:w-auto ${sortOpen ? "bg-[#F5F5F5]" : "bg-white hover:bg-[#F5F5F5]"}`}>
                    {SORT_OPTIONS.find((o) => o.id === sortBy).label}
                    {sortOpen ? <ChevronUp size={12} className="text-[#52525B]" /> : <ChevronDown size={12} className="text-[#52525B]" />}
                  </button>
                  {sortOpen && (
                    <div className={`absolute top-[38px] right-0 w-[170px] p-1.5 z-[60] ${SX.popover}`}>
                      {SORT_OPTIONS.map((o) => (
                        <div key={o.id} onClick={() => { setSortBy(o.id); setSortOpen(false); }}
                          className={`flex items-center justify-between gap-2 px-2.5 py-2 rounded-lg cursor-pointer text-[12.5px] font-medium transition-colors ${
                            o.id === sortBy ? "bg-[#FBF5FA] text-[#893F82]" : "text-[#1A1A1A] hover:bg-[#F5F5F5]"
                          }`}>
                          {o.label}
                          {o.id === sortBy && <Check size={12} />}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {loading ? (
              <div className="flex justify-center py-20 text-[#A04A97]"><Spinner size={26} /></div>
            ) : sorted.length === 0 ? (
              <div className="text-center py-16 text-[#8A8A90] text-[13.5px]">No templates match.</div>
            ) : (
              <div className="flex flex-col gap-2.5">{sorted.map((t) => <Row key={t.id} tpl={t} onOpen={onOpenTemplate} />)}</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudioLibrary;
